import { useEffect, useState } from 'react';

import { adminClient } from '@/api/adminClient';
import { AdminSection } from '@/components/shell/AdminSection';

import { OrgSwitcher } from '../Users/UsersPage';
import { usePersonNamesOf, useOrgChoice } from './names';
import { DENIAL_PAGE, DENIAL_PAGES, errorText, permissionsApi } from './permissionsApi';
import type { AskableIntegration, Grant } from './permissionsApi';
import { Problem, givenBy } from './shared';
import type { PermissionTab } from './PermissionsPage';

/** One number of the Overview: loading, a failure with the service's text, or the value. */
type Stat<T> = { state: 'loading' } | { state: 'error'; message: string } | { state: 'ready'; value: T };

function useStat<T>(load: () => Promise<T>, deps: unknown[]): Stat<T> {
  const [stat, setStat] = useState<Stat<T>>({ state: 'loading' });
  useEffect(() => {
    let cancelled = false;
    setStat({ state: 'loading' });
    load().then(
      (value) => !cancelled && setStat({ state: 'ready', value }),
      (failure) => !cancelled && setStat({ state: 'error', message: errorText(failure) }),
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `deps` is the list of inputs of `load`
  }, deps);
  return stat;
}

const DAY = 24 * 60 * 60 * 1000;

/** "N" or "N+" when the read stopped at its cap. */
export const countText = (n: number, capped: boolean) => (capped ? `${n}+` : String(n));

function StatCard({ label, stat, to, onGo, show, note }: { label: string; stat: Stat<number>; to: PermissionTab; onGo: (tab: PermissionTab) => void; show?: (n: number) => string; note?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card/40 p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      {stat.state === 'ready' ? (
        <button type="button" onClick={() => onGo(to)} aria-label={`${label}: open`} className="mt-1 text-2xl font-semibold text-foreground hover:underline">
          {show ? show(stat.value) : stat.value}
        </button>
      ) : stat.state === 'loading' ? (
        <p className="mt-1 text-2xl font-semibold text-muted-foreground">…</p>
      ) : (
        <p role="alert" className="mt-1 text-sm text-destructive">
          {stat.message}
        </p>
      )}
      {note ? <p className="mt-1 text-xs text-muted-foreground">{note}</p> : null}
    </div>
  );
}

/** Delegates per integration, by name. Integrations that use the admins are left out. */
function delegatesOf(integrations: AskableIntegration[]) {
  return integrations.flatMap((i) => (i.granter.kind === 'delegates' && i.granter.delegates?.length ? [{ id: i.id, people: i.granter.delegates }] : []));
}

export function OverviewTab({ onGo }: { onGo: (tab: PermissionTab) => void }) {
  const { orgs, orgId, choose, error: orgError } = useOrgChoice();
  const nameOf = usePersonNamesOf(orgId);
  // Without an org there is no count: the card loads or shows the error, and never reads 0.
  const counted = useStat(async () => (orgId ? (await adminClient.getTeamPeople({ orgId, access: 'everyone', page: 1, pageSize: 1 })).total : 0), [orgId]);
  const people: Stat<number> = orgError ? { state: 'error', message: orgError } : !orgId ? { state: 'loading' } : counted;
  const askable = useStat(() => permissionsApi.askable(), []);
  const requests = useStat(async () => {
    // The app list may be refused alone: then the access requests still count, as on the Requests tab.
    const [access, apps] = await Promise.all([permissionsApi.decidable(), permissionsApi.decidableApps().then((list) => list.length, () => undefined)]);
    return { total: access.length + (apps ?? 0), appsFailed: apps === undefined };
  }, []);
  const requestCount: Stat<number> = requests.state === 'ready' ? { state: 'ready', value: requests.value.total } : requests;
  const denials = useStat(() => permissionsApi.denialsSince(new Date(Date.now() - 7 * DAY).toISOString()), []);
  const admins = useStat(() => permissionsApi.admins(), []);

  const integrationCount: Stat<number> = askable.state === 'ready' ? { state: 'ready', value: askable.value.length } : askable;
  const denialCount: Stat<number> = denials.state === 'ready' ? { state: 'ready', value: denials.value.count } : denials;
  const capped = denials.state === 'ready' && denials.value.capped;

  return (
    <div className="space-y-6">
      {orgs.length > 0 ? <OrgSwitcher orgs={orgs} orgId={orgId} onChange={choose} /> : null}
      {orgError ? <Problem message={orgError} /> : null}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="People" stat={people} to="people" onGo={onGo} />
        <StatCard label="Integrations" stat={integrationCount} to="integrations" onGo={onGo} />
        <StatCard label="Open requests" stat={requestCount} to="requests" onGo={onGo} note={requests.state === 'ready' && requests.value.appsFailed ? 'App requests could not load.' : undefined} />
        <StatCard label="Denials in 7 days" stat={denialCount} to="log" onGo={onGo} show={(n) => countText(n, capped)} />
      </div>
      {capped ? <p className="text-xs text-muted-foreground">The count stops after {DENIAL_PAGE * DENIAL_PAGES} denials.</p> : null}
      <div className="grid gap-6 lg:grid-cols-2">
        <AdminSection title="Admin grants" description="Who holds an admin grant on the platform.">
          {admins.state === 'loading' ? <p className="text-sm text-muted-foreground">Loading admins</p> : null}
          {admins.state === 'error' ? <Problem message={admins.message} /> : null}
          {admins.state === 'ready' && admins.value.admins.length === 0 ? <p className="text-sm text-muted-foreground">No admin grants found.</p> : null}
          {admins.state === 'ready' ? (
            <ul className="space-y-1.5 text-sm">
              {admins.value.admins.map((a: Grant) => (
                <li key={a.id}>
                  {a.subject_label || `${a.subject} (no name)`} <span className="text-xs text-muted-foreground">given by {givenBy(a)}</span>
                </li>
              ))}
              {admins.value.more ? <li className="text-xs text-muted-foreground">More admin grants exist than this list shows.</li> : null}
            </ul>
          ) : null}
          <p className="mt-3 text-xs text-muted-foreground">Admins added by bootstrap or WorkOS sync may not appear here.</p>
        </AdminSection>
        <AdminSection title="Delegates" description="Who decides the requests for an integration. The other integrations use the admins.">
          {askable.state === 'loading' ? <p className="text-sm text-muted-foreground">Loading delegates</p> : null}
          {askable.state === 'error' ? <Problem message={askable.message} /> : null}
          {askable.state === 'ready' && delegatesOf(askable.value).length === 0 ? <p className="text-sm text-muted-foreground">No integration has a delegate.</p> : null}
          {askable.state === 'ready' ? (
            <ul className="space-y-1.5 text-sm">
              {delegatesOf(askable.value).map((d) => (
                <li key={d.id}>
                  <span className="font-medium">{d.id}</span> <span className="text-muted-foreground">{d.people.map(nameOf).join(', ')}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </AdminSection>
      </div>
    </div>
  );
}
